import React, { useMemo, useState } from 'react';
import { Alert, Empty, Modal, Spin, Statistic, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useDevMode } from '@/contexts/DevModeContext';
import { COLORS, formatUnit } from '../../../utils/constants';
import type {
  SectionMatrix,
  SectionMatrixCellLine,
  SectionMatrixColumn,
  SectionMatrixRow,
} from '../../../utils/siteWorksMatrixGenerator';

const { Text, Paragraph } = Typography;

const fmtCurrency = (n: number): string =>
  `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtVolume = (n: number): string =>
  n.toLocaleString('en-GB', { minimumFractionDigits: 3, maximumFractionDigits: 3 });

const fmtQty = (n: number): string =>
  n.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 3 });

/** Column / grand totals are 2dp-rounded before display, same as the Plot Matrix. */
const round2 = (n: number): number => Math.round(n * 100) / 100;

interface SectionMatrixTableProps {
  matrix: SectionMatrix;
  loading?: boolean;
}

/**
 * Renders one non-drainage section matrix — Roads, Main Services, Substation,
 * POS, Compound or Spoil. Deliberately plain: a stat line, the grid, a footnote.
 * Every matrix has the same shape (`SectionMatrix`), so this one table serves
 * all of them and they all read the same way.
 */
const SectionMatrixTable: React.FC<SectionMatrixTableProps> = ({ matrix, loading = false }) => {
  const showDevMode = useDevMode();
  const [cellModal, setCellModal] = useState<{ row: SectionMatrixRow; column: SectionMatrixColumn } | null>(
    null,
  );

  const isVolume = matrix.valueKind === 'volume';
  const fmtValue = isVolume ? fmtVolume : fmtCurrency;
  const unitRows = matrix.rows.filter((row) => row.rowKind === 'unit');
  const unpriced = matrix.measuredItems - matrix.pricedItems;
  // A band row's label spans the label cell plus every per-category column —
  // stopping short of the Total column, which shows the band's own section
  // total (e.g. Pump Station's vs Rising Main's) instead of being blank.
  const bandLabelSpan = 1 + matrix.columns.length;

  const columns: ColumnsType<SectionMatrixRow> = useMemo(() => {
    const groups: Array<{ group: string; columns: SectionMatrixColumn[] }> = [];
    matrix.columns.forEach((col) => {
      const last = groups[groups.length - 1];
      if (last && last.group === col.group) last.columns.push(col);
      else groups.push({ group: col.group, columns: [col] });
    });

    return [
      {
        title: matrix.unitHeader,
        dataIndex: 'label',
        key: 'label',
        width: 240,
        fixed: 'left' as const,
        onCell: (record: SectionMatrixRow) =>
          record.rowKind === 'band' ? { colSpan: bandLabelSpan } : {},
        render: (label: string, record: SectionMatrixRow) => {
          if (record.rowKind === 'band') {
            return (
              <strong style={{ fontSize: 13, color: '#374151', letterSpacing: '0.05em' }}>
                {label.toUpperCase()}
              </strong>
            );
          }
          return (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Text strong>{label}</Text>
              {record.sublabel && (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {record.sublabel}
                </Text>
              )}
            </div>
          );
        },
      },
      ...groups.map((group) => ({
        title: group.group,
        key: `group-${group.group}`,
        align: 'center' as const,
        children: group.columns.map((col) => ({
          title: (
            <Tooltip title={col.description}>
              <span style={{ color: '#1890ff', fontWeight: 600 }}>{col.label}</span>
            </Tooltip>
          ),
          key: col.key,
          width: 140,
          align: 'right' as const,
          onCell: (record: SectionMatrixRow) =>
            record.rowKind === 'band' ? { colSpan: 0 } : {},
          render: (_: unknown, record: SectionMatrixRow) => {
            if (record.rowKind === 'band') return null;
            const cell = record.cells[col.key];
            if (!cell || cell.notMeasured) return null;
            if (cell.lines.length === 0) return <Text type="secondary">—</Text>;
            return (
              <Text
                style={{
                  cursor: 'pointer',
                  textDecoration: 'underline dotted',
                  textUnderlineOffset: 3,
                  color: cell.total < 0 ? '#cf1322' : undefined,
                }}
                onClick={() => setCellModal({ row: record, column: col })}
              >
                {fmtValue(cell.total)}
              </Text>
            );
          },
        })),
      })),
      {
        title: matrix.totalHeader,
        key: 'total',
        width: 150,
        align: 'right' as const,
        render: (_: unknown, record: SectionMatrixRow) => (
          <Text
            strong
            style={{
              color: record.total < 0 ? '#cf1322' : undefined,
              fontSize: record.rowKind === 'band' ? 13 : undefined,
            }}
          >
            {fmtValue(record.total)}
          </Text>
        ),
      },
    ];
  }, [matrix, fmtValue, bandLabelSpan]);

  const cellLines: SectionMatrixCellLine[] = cellModal
    ? cellModal.row.cells[cellModal.column.key]?.lines ?? []
    : [];

  const lineColumns: ColumnsType<SectionMatrixCellLine & { key: number }> = [
    {
      title: 'Item',
      dataIndex: 'description',
      key: 'description',
      render: (value: string, record: SectionMatrixCellLine) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Text>{value}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {record.source}
            {!isVolume && record.rate == null && ' · no item-library rate yet'}
          </Text>
          {showDevMode && record.evaluateCode && (
            <Text style={{ fontSize: 10, color: '#999' }}>{record.evaluateCode}</Text>
          )}
        </div>
      ),
    },
    {
      title: 'Qty',
      dataIndex: 'qty',
      key: 'qty',
      width: 110,
      align: 'right',
      render: (value: number) => fmtQty(value),
    },
    {
      title: 'Unit',
      dataIndex: 'unit',
      key: 'unit',
      width: 70,
      align: 'center',
      render: (value: string) => formatUnit(value) || '',
    },
    ...(isVolume
      ? []
      : [
          {
            title: 'Rate (£)',
            dataIndex: 'rate',
            key: 'rate',
            width: 110,
            align: 'right' as const,
            render: (value: number | undefined) =>
              value == null ? <Text type="secondary">—</Text> : fmtQty(value),
          },
          {
            title: 'Amount (£)',
            dataIndex: 'amount',
            key: 'amount',
            width: 130,
            align: 'right' as const,
            render: (value: number) => <Text strong>{fmtCurrency(value)}</Text>,
          },
        ]),
  ];

  if (unitRows.length === 0) {
    return (
      <div style={{ padding: '32px' }}>
        <Empty description={matrix.emptyHint} />
      </div>
    );
  }

  return (
    <div>
      <style>{`
        .section-matrix-table .ant-table { border-radius: 0 !important; }
        .section-matrix-table .ant-table-container { border-radius: 0 !important; }
        .section-matrix-table .ant-table-thead > tr > th {
          background-color: ${COLORS.tableHeaderBg} !important;
          text-align: center;
        }
        .section-matrix-row-band > td {
          background-color: #d1d5db !important;
          padding-top: 6px !important;
          padding-bottom: 6px !important;
        }
      `}</style>

      {/* ── Headline figures ─────────────────────────────────────────────── */}
      <div
        style={{
          display: 'flex',
          gap: 24,
          flexWrap: 'wrap',
          alignItems: 'center',
          padding: '12px 16px',
          border: '1px solid #e5e7eb',
          borderRadius: 6,
          background: '#fbfcfe',
          marginBottom: 12,
        }}
      >
        <Statistic
          title={`${matrix.unitHeader}s`}
          value={unitRows.length}
          valueStyle={{ fontSize: 20 }}
        />
        <Statistic
          title={isVolume ? 'Net surplus' : 'Section total'}
          value={isVolume ? `${fmtVolume(matrix.grandTotal)} m³` : fmtCurrency(matrix.grandTotal)}
          valueStyle={{ fontSize: 20, color: COLORS.primary }}
        />
        <Statistic
          title={isVolume ? 'Areas measured' : 'Items measured'}
          value={isVolume ? matrix.measuredItems : `${matrix.pricedItems} / ${matrix.measuredItems} priced`}
          valueStyle={{ fontSize: 20 }}
        />
        {!isVolume && (
          <Tooltip
            title={
              Math.abs(matrix.variance) < 0.01
                ? `Every priced item in the ${matrix.title} Bill Summary is accounted for in this matrix.`
                : `The matrix is out by ${fmtCurrency(matrix.variance)} against the ${matrix.title} Bill Summary total.`
            }
          >
            <Tag color={Math.abs(matrix.variance) < 0.01 ? 'success' : 'error'}>
              {Math.abs(matrix.variance) < 0.01
                ? 'Ties to Bill Summary'
                : `Variance ${fmtCurrency(matrix.variance)}`}
            </Tag>
          </Tooltip>
        )}
      </div>

      {!isVolume && unpriced > 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={`${unpriced} of ${matrix.measuredItems} measured items have no item-library rate yet, so they carry £0 here — open any cell to see the measured quantities behind it.`}
        />
      )}

      <div className="section-matrix-table">
        {loading ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '80px 0',
              gap: 16,
            }}
          >
            <Spin size="large" />
            <span style={{ color: '#6B7280', fontSize: 14 }}>Loading rate library…</span>
          </div>
        ) : (
          <Table<SectionMatrixRow>
            columns={columns}
            dataSource={matrix.rows}
            rowKey="key"
            bordered
            size="middle"
            pagination={false}
            scroll={{ x: 'max-content' }}
            sticky={{ offsetHeader: 80 }}
            rowClassName={(record) => (record.rowKind === 'band' ? 'section-matrix-row-band' : '')}
            summary={() => (
              <Table.Summary.Row style={{ backgroundColor: '#f0f0f0', fontWeight: 'bold' }}>
                <Table.Summary.Cell index={0}>
                  <strong>GRAND TOTAL</strong>
                </Table.Summary.Cell>
                {matrix.columns.map((col, i) => (
                  <Table.Summary.Cell key={col.key} index={1 + i} align="right">
                    <strong>{fmtValue(round2(matrix.columnTotals[col.key] ?? 0))}</strong>
                  </Table.Summary.Cell>
                ))}
                <Table.Summary.Cell index={1 + matrix.columns.length} align="right">
                  <strong style={{ fontSize: 16 }}>{fmtValue(round2(matrix.grandTotal))}</strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />
        )}
      </div>

      <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 12 }}>
        {matrix.note}
      </Paragraph>

      <Modal
        open={!!cellModal}
        onCancel={() => setCellModal(null)}
        footer={null}
        width={820}
        title={cellModal ? `${cellModal.row.label} · ${cellModal.column.label}` : ''}
      >
        {cellModal && (
          <>
            <Paragraph type="secondary" style={{ fontSize: 13 }}>
              {cellModal.column.description}
            </Paragraph>
            <Table<SectionMatrixCellLine & { key: number }>
              size="small"
              bordered
              pagination={false}
              dataSource={cellLines.map((line, i) => ({ ...line, key: i }))}
              columns={lineColumns}
              summary={() => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={isVolume ? 2 : 4}>
                    <Text strong>Cell total</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <Text strong>
                      {fmtValue(cellModal.row.cells[cellModal.column.key]?.total ?? 0)}
                    </Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
          </>
        )}
      </Modal>
    </div>
  );
};

export default SectionMatrixTable;
